import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixEcomV1BackInStockSettings from './ecom-v1-back-in-stock-settings-back-in-stock-settings.http.js';

/**
 * The back in stock settings object holds information related to the state of collecting back in stock
 * [notification requests](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/other-services/back-in-stock/back-in-stock-notifications/introduction).
 */
export interface BackInStockSettings {
  /**
   * Information about collecting customer requests to receive
   * back in stock notifications.
   */
  collectionStates?: BackInStockCollectionState[];
}

export interface BackInStockCollectionState {
  /**
   * ID of the app to receive notification requests for.
   *
   * Back in stock notifications only work for [Wix Stores](https://support.wix.com/en/article/wix-stores-about-wix-stores).
   * The `appId` for Wix Stores is `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`.
   * @format GUID
   */
  appId?: string;
  /** Whether to collect requests for items from this app. */
  collectingRequests?: boolean;
}

export interface StartCollectingRequestsRequest {
  /**
   * ID of the app to start accepting notification requests for.
   *
   * Back in stock notifications only work for [Wix Stores](https://support.wix.com/en/article/wix-stores-about-wix-stores).
   * The `appId` for Wix Stores is `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`.
   * @format GUID
   */
  appId: string;
}

export interface StartCollectingRequestsResponse {
  /** Back in stock settings info. */
  settings?: BackInStockSettings;
}

export interface StopCollectingRequestsRequest {
  /**
   * ID of the app to stop accepting notification requests for.
   *
   * Back in stock notifications only work for [Wix Stores](https://support.wix.com/en/article/wix-stores-about-wix-stores).
   * The `appId` for Wix Stores is `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`.
   * @format GUID
   */
  appId: string;
}

export interface StopCollectingRequestsResponse {
  /** Back in stock settings info. */
  settings?: BackInStockSettings;
}

export interface GetSettingsRequest {}

export interface GetSettingsResponse {
  /** Retrieved back in stock request settings. */
  settings?: BackInStockSettings;
}

/** @docsIgnore */
export type StartCollectingRequestsApplicationErrors = {
  code?: 'NOT_SUPPORTED_APP_DEF_ID';
  description?: string;
  data?: Record<string, any>;
};

/**
 * Sets `settings.collectionStates.collectingRequests` to `true` for given `appId`.
 *
 * When the collection state is set to `true`, collecting requests is enabled and customers may request
 * notifications for out of stock products. While collecting is enabled, customers see a "Notify When Available"
 * button on out-of-stock items. Customers can click the button to enter their email address, which creates the
 * notification request.
 * @param appId - ID of the app to start accepting notification requests for.
 *
 * Back in stock notifications only work for [Wix Stores](https://support.wix.com/en/article/wix-stores-about-wix-stores).
 * The `appId` for Wix Stores is `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`.
 * @public
 * @requiredField appId
 * @permissionId ECOM.MODIFY_BACK_IN_STOCK_NOTIFICATION_SETTINGS
 * @applicableIdentity APP
 * @fqn com.wixpress.ecom.back.in.stock.BackInStockSettingsService.StartCollectingRequests
 */
export async function startCollectingRequests(appId: string): Promise<
  NonNullablePaths<
    StartCollectingRequestsResponse,
    | `settings.collectionStates`
    | `settings.collectionStates.${number}.appId`
    | `settings.collectionStates.${number}.collectingRequests`,
    5
  > & {
    __applicationErrorsType?: StartCollectingRequestsApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ appId: appId });

  const reqOpts =
    ambassadorWixEcomV1BackInStockSettings.startCollectingRequests(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { appId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['appId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Sets `settings.collectionStates.collectingRequests` to `false` for given `appId`.
 *
 * When the collection state is set to `false`, collecting notification requests is disabled.
 * @param appId - ID of the app to stop accepting notification requests for.
 *
 * Back in stock notifications only work for [Wix Stores](https://support.wix.com/en/article/wix-stores-about-wix-stores).
 * The `appId` for Wix Stores is `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`.
 * @public
 * @requiredField appId
 * @permissionId ECOM.MODIFY_BACK_IN_STOCK_NOTIFICATION_SETTINGS
 * @applicableIdentity APP
 * @fqn com.wixpress.ecom.back.in.stock.BackInStockSettingsService.StopCollectingRequests
 */
export async function stopCollectingRequests(
  appId: string
): Promise<
  NonNullablePaths<
    StopCollectingRequestsResponse,
    | `settings.collectionStates`
    | `settings.collectionStates.${number}.appId`
    | `settings.collectionStates.${number}.collectingRequests`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ appId: appId });

  const reqOpts =
    ambassadorWixEcomV1BackInStockSettings.stopCollectingRequests(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { appId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['appId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves back in stock notification request settings.
 * @public
 * @permissionId ECOM.READ_BACK_IN_STOCK_NOTIFICATION_SETTINGS
 * @applicableIdentity APP
 * @fqn com.wixpress.ecom.back.in.stock.BackInStockSettingsService.GetSettings
 */
export async function getSettings(): Promise<
  NonNullablePaths<
    GetSettingsResponse,
    | `settings.collectionStates`
    | `settings.collectionStates.${number}.appId`
    | `settings.collectionStates.${number}.collectingRequests`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({});

  const reqOpts = ambassadorWixEcomV1BackInStockSettings.getSettings(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {},
        singleArgumentUnchanged: false,
      },
      []
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
